import Auth from './auth';
var Operation;
(function (Operation) {
    Operation["ADD"] = "add";
    Operation["REMOVE"] = "remove";
})(Operation || (Operation = {}));
export default class WishlistStorage {
    /**
     * Widoki WebView nie wspierają localStorage, sprawdź czy aktualna przeglądarka go obsługuje
     */
    static isSupported() {
        return typeof localStorage !== 'undefined' && localStorage !== null;
    }
    static async acquireLock() {
        if (this.lock === 0) {
            this.lock = Date.now();
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.waitingLocks.push(resolve);
        });
    }
    static releaseLock() {
        this.lock = 0;
        if (this.waitingLocks.length > 0) {
            const lastResolve = this.waitingLocks.shift();
            if (typeof lastResolve !== 'undefined') {
                this.lock = Date.now();
                lastResolve();
            }
        }
    }
    /**
     * Dodaj produkt do wishlisty
     * @param sku SKU produktu
     * @param altshop_id
     */
    static async add(sku, altshop_id) {
        const data = this.getCurrentStorage();
        if (!data.includes(sku)) {
            data.push(sku);
            this.store(data);
            await this.acquireLock();
            // Dodanie operacji do stosu
            this.defer({
                operation: Operation.ADD,
                sku,
                altshop_id,
            });
            // Wysłanie operacji w takiej kolejności, w jakiej się pojawiały w celu zachowania kolejności
            await this.sync(false);
            // Poinformowanie innych komponentów o zmianie stanu SKU
            this.triggerListeners(sku);
            this.releaseLock();
        }
    }
    /**
     * Usuń produkt z wishlisty
     * @param sku SKU produktu
     * @param altshop_id
     */
    static async remove(sku, altshop_id) {
        const data = this.getCurrentStorage();
        if (data.includes(sku)) {
            data.splice(data.indexOf(sku), 1);
            this.store(data);
            await this.acquireLock();
            // Dodanie operacji do stosu
            this.defer({
                operation: Operation.REMOVE,
                sku,
                altshop_id,
            });
            // Wysłanie operacji w takiej kolejności, w jakiej się pojawiały w celu zachowania kolejności
            await this.sync(false);
            // Poinformowanie innych komponentów o zmianie stanu SKU
            this.triggerListeners(sku);
            this.releaseLock();
        }
    }
    /**
     * Synchronizuj wishlistę: wykonaj zakolejkowane operacje a na koniec pobierz aktualny stan wishlisty z backendu
     */
    static async sync(syncAll = true) {
        // Dla niezalogowanego nie mamy co próbować wysyłać requesty / możemy jedynie przechowywać stan lokalnie
        if (!this.isLoggedIn()) {
            return;
        }
        const operations = this.getDeferredOperations();
        try {
            const removeOperationsSkus = operations
                .filter((operation) => operation.operation === Operation.REMOVE)
                .map((operation) => operation.sku);
            //Grupowanie operacji dodania po altshop_id
            const addOperations = operations.filter((operation) => operation.operation === Operation.ADD);
            const addOperationsGrouped = new Map();
            addOperations.forEach((operation) => {
                if (!addOperationsGrouped.has(operation.altshop_id)) {
                    addOperationsGrouped.set(operation.altshop_id, []);
                }
                addOperationsGrouped.get(operation.altshop_id)?.push(operation.sku);
            });
            if (removeOperationsSkus.length > 0) {
                await fetch(`/api/wishlist/default/products/${encodeURIComponent(removeOperationsSkus.join(','))}`, {
                    method: 'DELETE',
                    cache: 'no-cache',
                });
            }
            for (const [altshop_id, skus] of addOperationsGrouped) {
                await fetch('/api/wishlist/default/products', {
                    method: 'POST',
                    cache: 'no-cache',
                    body: JSON.stringify({ skus: skus, altshop_id }),
                    headers: { 'content-type': 'application/json' },
                });
            }
        }
        catch (e) {
            // Błąd komunikacji sieciowej, wstrzymujemy się
            return;
        }
        // Wykonaliśmy wszystkie operacje, czyścimy bufor
        this.clearDeferredOperations();
        // Pobierz aktualną listę produktów z backendu
        if (syncAll) {
            try {
                await this.loadProductsList();
            }
            catch (e) {
                // Nie udało się zaktualizować listy produktów, wymuszamy jak najszybszą synchronizację
                localStorage.removeItem('wishlist_sync_time');
                return;
            }
            localStorage.setItem('wishlist_sync_time', Math.floor(Date.now() / 1000).toFixed());
        }
    }
    /**
     * Zarejestruj funkcję synchronizującą stan wishlisty, w tym uzupełniającą stan wishlisty, jeżeli nie był
     * nigdy aktualizowany
     */
    static registerSync() {
        if (this.syncRegistered) {
            return;
        }
        this.syncRegistered = true;
        // Weryfikacja last auth state: triggerowanie zdarzenia zmiany stanu autoryzacji
        const stateChanged = this.checkAuthStateChange();
        // Jeżeli ostatni czas synchronizacji to ponad 5 minut lub nie była wykonana nigdy: dokonujemy synchronizacji
        // Jeżeli stan się zmienił to synchronizacja i tak zostanie uruchomiona
        if (!stateChanged && this.isLoggedIn()) {
            const lastSyncTime = localStorage.getItem('wishlist_sync_time');
            if (lastSyncTime === null ||
                parseInt(lastSyncTime) < Math.floor(Date.now() / 1000) - 300 ||
                this.getDeferredOperations().length > 0) {
                this.sync().catch((err) => {
                    console.error('Wishlist synchronization failed', err);
                });
            }
        }
        // Obsługa zmiany statusu autoryzacji podczas pobytu na stronie (bez zmiany lokalizacji)
        Auth.addListener(() => {
            this.checkAuthStateChange();
        });
    }
    static isLoggedIn() {
        const state = Auth.getLastState();
        if (state === null) {
            return false;
        }
        return state.loggedIn;
    }
    static checkAuthStateChange() {
        const isLoggedIn = this.isLoggedIn() ? '1' : '0';
        if (localStorage.getItem('wishlist_last_auth_state') === isLoggedIn) {
            return false;
        }
        localStorage.setItem('wishlist_last_auth_state', isLoggedIn);
        if (isLoggedIn === '1') {
            // Użytkownik zalogował się, synchronizujemy wishlistę
            this.sync().catch((err) => {
                console.error('Wishlist synchronization failed', err);
            });
        }
        else {
            // Wyczyszczenie local storage/wishlisty
            const oldWishlist = this.getCurrentStorage();
            localStorage.removeItem('wishlist');
            localStorage.removeItem('wishlist_defer');
            localStorage.removeItem('wishlist_sync_time');
            // Aktualizacja komponentów
            oldWishlist.forEach((sku) => {
                this.triggerListeners(sku);
            });
        }
        return true;
    }
    /**
     * Załaduj listę produktów z backendu i zaktualizuj stan komponentów zawierających dane SKU
     */
    static async loadProductsList() {
        const response = await fetch('/api/v1/wishlist/default/products?fields=sku&per_page=99999', {
            method: 'GET',
            cache: 'no-cache',
        });
        if (!response.ok) {
            return;
        }
        const data = (await response.json());
        // Lista SKU, dla których musimy zakomunikować zmianę stanu
        const changed = [];
        const previousList = this.getCurrentStorage();
        const newList = [];
        // Zapisujemy nową listę SKU
        data.products.forEach((product) => {
            newList.push(product.sku);
            changed.push(product.sku);
        });
        // Sprawdzamy jakie produkty zniknęły z listy
        previousList.forEach((sku) => {
            if (!newList.includes(sku)) {
                // Produkt został usunięty przez innego klienta
                changed.push(sku);
            }
        });
        // Aktualizacja stanu
        this.store(newList);
        // Propagowanie zmiany do elementów
        changed.forEach((sku) => {
            this.triggerListeners(sku);
        });
    }
    /**
     * Sprawdź, czy produkt istnieje na wishliście
     * @param sku SKU produktu
     */
    static has(sku) {
        return this.getCurrentStorage().includes(sku);
    }
    /**
     * Dodaj funkcję nasłuchującą na zmianę stanu elementu na wishliście
     * @param fn Funkcja wykonywana w momencie zmiany stanu dowolnego elementu wishlisty
     */
    static addListener(fn) {
        this.listeners.push(fn);
    }
    /**
     * Zakomunikuj nasłuchującym elmentom o zmianie stanu produktu na wishliście
     * @param sku SKU produktu ze zmienionym stanem na wishliście
     */
    static triggerListeners(sku) {
        this.listeners.forEach((fn) => {
            fn(sku);
        });
    }
    /**
     * Pobierz aktualną wishlistę na podstawie localStorage
     */
    static getCurrentStorage() {
        return this.getFromStorage('wishlist');
    }
    /**
     * Zaktualizuj wishlistę w localStorage
     * @param data string[] Lista SKU produktów na wishliście
     */
    static store(data) {
        this.setInStorage('wishlist', data);
    }
    /**
     * Dodaj akcje na wishliście do zakolejkowania
     * @param newAction Akcja dodania/usunięcia SKU z wishlisty
     */
    static defer(newAction) {
        let data = this.getDeferredOperations();
        data.push(newAction);
        // Optymalizacja: deduplikacja operacji dla tego samego SKU
        const buffer = new Map();
        data.forEach((action) => {
            const lastAction = buffer.get(newAction.sku);
            if (typeof lastAction !== 'undefined') {
                // Istnieje akcja dla tego SKU
                if (action.operation !== lastAction.operation) {
                    // Dodajemy po usunięciu lub usuwamy po dodaniu - nie ma sensu żadna z tych operacji
                    buffer.delete(action.sku);
                }
                else {
                    // Jest to dokładnie ta sama akcja co wcześniej - pomijamy ją
                }
                return;
            }
            // SKU nie było dodawane lub usuwane, zapisujemy operację
            buffer.set(action.sku, action);
        });
        // Zbudowanie finalnej tablicy na podstawie zdeduplikowanych informacji o finalnej operacji
        data = [];
        buffer.forEach((action) => {
            data.push(action);
        });
        localStorage.setItem('wishlist_defer', JSON.stringify(data));
    }
    /**
     * Pobierz aktualną kolejkę operacji na wishliście
     */
    static getDeferredOperations() {
        const raw = localStorage.getItem('wishlist_defer');
        let data = [];
        if (raw !== null) {
            data = JSON.parse(raw);
        }
        return data;
    }
    /**
     * Wyczyść kolejkę operacji na wishliście
     */
    static clearDeferredOperations() {
        localStorage.removeItem('wishlist_defer');
    }
    static getFromStorage(key) {
        const raw = localStorage.getItem(key);
        let data = [];
        if (raw !== null) {
            data = JSON.parse(raw);
        }
        return data;
    }
    static setInStorage(key, data) {
        localStorage.setItem(key, JSON.stringify(data));
    }
}
WishlistStorage.listeners = [];
WishlistStorage.syncRegistered = false;
WishlistStorage.lock = 0;
WishlistStorage.waitingLocks = [];
